import React, { useEffect, useState } from 'react';
import { ChevronRight, Loader, Lock, Shield, Tag, CheckCircle, X, Crown, CreditCard } from 'lucide-react';
import Swal from 'sweetalert2';
import { formatCardNumber, formatExpiry, formatCVV, getMembershipPlans, getSpecialtyPrice } from '../../utils/checkoutHelpers';
import {
    processFullMembershipPayment,
    validateMembershipCoupon,
    showPaymentError,
} from '../../utils/paymentService';
import { validateCardData } from '../../utils/checkoutHelpers';
import { fetchVisibleMemberships, periodLabel } from '../../services/FirebaseService';
import { pixelInitiateCheckout, pixelPurchase, generateEventId } from '../../lib/pixel';

// Meses por periodo (fallback local cuando el backend no responde).
const PLAN_MONTHS = { monthly: 1, quarterly: 3, annual: 12 };

// Helper: resuelve el id real de la membresía soportando varios shapes de plan.
// Las plans del codebase usan `odooId` pero código viejo a veces espera
// `membershipId`. Esto evita que membership_id sea `null` en la llamada al backend.
const resolveMembershipId = (plan) =>
    plan?.membershipId ?? plan?.odooId ?? (typeof plan?.id === 'number' ? plan.id : null);

// Lista de fallback (la fuente real es is_shown del backend). Se usa solo si
// fetchVisibleMemberships() devuelve vacío. Shape idéntico al del backend.
const buildFallbackPlans = () =>
    Object.entries(getMembershipPlans(getSpecialtyPrice())).map(([key, p]) => ({
        id: p.odooId,
        odooId: p.odooId,
        membershipId: p.odooId,
        name: p.name,
        price: p.price,
        months: PLAN_MONTHS[key] || 1,
        monthlyPrice: Math.round(p.price / (PLAN_MONTHS[key] || 1)),
        isRecommended: !!p.popular,
        features: p.features || [],
        order: PLAN_MONTHS[key] || 0,
    }));

const MembershipCheckout = ({ membershipPlan, userId, onSuccess, onBack }) => {
    const [loading, setLoading] = useState(false);

    // Meta Pixel: inicia el pago de la suscripción.
    useEffect(() => {
        pixelInitiateCheckout();
    }, []);
    const [cardData, setCardData] = useState({
        number: '',
        name: '',
        expiry: '',
        cvv: ''
    });

    // ─── Plan selector embebido ───
    // La lista de paquetes elegibles viene del backend: TODOS los dais_membership
    // de suscripción con is_shown == true (ordenados por `order`). Mientras carga
    // se muestra el fallback local; si el fetch falla, se queda con él.
    const [plans, setPlans] = useState(buildFallbackPlans);
    useEffect(() => {
        let cancelled = false;
        fetchVisibleMemberships()
            .then((list) => {
                if (!cancelled && Array.isArray(list) && list.length > 0) setPlans(list);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Plan seleccionado por id. Respeta el plan entrante si existe en la lista;
    // si no, cae al recomendado o al primero. Conserva la elección manual del
    // usuario si sigue válida cuando llega la lista del backend.
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    useEffect(() => {
        setSelectedPlanId((prev) => {
            if (prev && plans.some((p) => p.id === prev)) return prev;
            const incoming = resolveMembershipId(membershipPlan);
            if (incoming && plans.some((p) => p.id === incoming)) return incoming;
            const rec = plans.find((p) => p.isRecommended);
            return (rec || plans[0])?.id ?? null;
        });
    }, [plans, membershipPlan]);

    const currentPlan = plans.find((p) => p.id === selectedPlanId) || plans[0] || null;
    const currentMembershipId = currentPlan?.membershipId ?? resolveMembershipId(currentPlan);

    // Coupon state
    const [couponCode, setCouponCode] = useState('');
    const [validatingCoupon, setValidatingCoupon] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [showCoupon, setShowCoupon] = useState(false);

    // Success state
    const [paymentDone, setPaymentDone] = useState(false);

    const originalAmount = parseFloat(currentPlan?.price) || 0;

    const calculateFinalAmount = () => {
        if (!appliedCoupon) return originalAmount;
        if (appliedCoupon.discountType === 'amount') {
            return Math.max(0, originalAmount - appliedCoupon.discountAmount);
        } else if (appliedCoupon.discountType === 'percentage') {
            return Math.max(0, originalAmount * (1 - appliedCoupon.discountPercentage / 100));
        }
        return originalAmount;
    };

    const totalAmount = calculateFinalAmount();

    const handleCardInputChange = (field, value) => {
        let formatted = value;
        if (field === 'number') formatted = formatCardNumber(value);
        else if (field === 'expiry') formatted = formatExpiry(value);
        else if (field === 'cvv') formatted = formatCVV(value);
        setCardData(prev => ({ ...prev, [field]: formatted }));
    };

    const handleValidateCoupon = async () => {
        if (!couponCode.trim()) {
            setCouponError('Ingresa un código');
            return;
        }
        setValidatingCoupon(true);
        setCouponError('');

        try {
            if (!currentMembershipId) {
                setCouponError('No se pudo determinar la membresía. Recarga la página.');
                setValidatingCoupon(false);
                return;
            }
            const result = await validateMembershipCoupon(
                couponCode,
                currentMembershipId,
                userId,
                null
            );

            if (result.success) {
                setAppliedCoupon(result.coupon);
                setCouponCode('');
            } else {
                setCouponError(result.error || 'Cupón no válido');
            }
        } catch {
            setCouponError('Error al validar el cupón');
        } finally {
            setValidatingCoupon(false);
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
        setCouponError('');
    };

    const handlePayment = async () => {
        const validation = validateCardData(cardData);
        if (!validation.isValid) {
            return showPaymentError(validation.message);
        }

        setLoading(true);

        try {
            if (!userId) {
                throw new Error('No se encontró información de usuario. Por favor inicia sesión.');
            }
            if (!currentMembershipId) {
                throw new Error('No se pudo determinar la membresía a comprar. Recarga la página.');
            }

            const couponIds = appliedCoupon ? [appliedCoupon.id] : [];

            const result = await processFullMembershipPayment(
                cardData,
                currentMembershipId,
                userId,
                couponIds
            );

            if (!result.success) {
                throw new Error(result.error);
            }

            // Meta Pixel Purchase: SOLO con el cargo confirmado. eventID (uuid
            // v4) para deduplicar contra la Conversions API cuando Odoo mande
            // el mismo evento server-side. Sin PII en los parámetros (salud).
            pixelPurchase({
                value: totalAmount,
                currency: 'MXN',
                plan: currentPlan?.name || 'Membresía',
                eventId: generateEventId(),
            });

            setPaymentDone(true);

            await Swal.fire({
                icon: 'success',
                title: '¡Membresía activada!',
                text: `Tu ${currentPlan?.name || 'membresía'} ha sido activada exitosamente`,
                showConfirmButton: false,
                timer: 2500
            });

            if (onSuccess) {
                onSuccess({
                    success: true,
                    plan: currentPlan,
                    cardLast4: result.cardLast4,
                    amount: totalAmount
                });
            }

        } catch (error) {
            let msg = 'Error al procesar el pago. ';
            if (error.message.includes('tarjeta')) msg += error.message;
            else if (error.message.includes('token')) msg += 'Verifica los datos de tu tarjeta.';
            else if (error.message.includes('autenticación') || error.message.includes('sesión')) msg += 'Sesión expirada. Recarga la página.';
            else msg += error.message || 'Intenta de nuevo o contacta a soporte.';
            showPaymentError(msg);
        } finally {
            setLoading(false);
        }
    };

    // ── Success state ──
    if (paymentDone) {
        return (
            <div className="max-w-lg mx-auto px-4 py-12 animate-fadeIn text-center">
                <div className="bg-surface-card rounded-card p-8">
                    <span className="flex items-center justify-center mx-auto mb-4 w-16 h-16 rounded-full bg-mint-pill text-mint-deep">
                      <CheckCircle size={32} aria-hidden="true" />
                    </span>
                    <h2 className="text-2xl font-bold text-text-primary mb-3">¡Bienvenida a Daisies!</h2>
                    <p className="text-text-secondary mb-6">Tu {currentPlan?.name || 'membresía'} ya está activa. Disfruta de tus consultas.</p>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="px-8 py-3 bg-daisies text-white rounded-card font-semibold hover:bg-daisies-hover transition-all"
                    >
                        Ir al inicio
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-4 py-6 animate-fadeIn">
            {/* Back */}
            <button
                onClick={onBack}
                className="mb-5 text-daisies hover:text-daisies-hover font-medium flex items-center gap-1.5 transition-all text-sm active:scale-[0.97]"
            >
                <ChevronRight className="rotate-180" size={15} />
                Volver
            </button>

            {/* ─── Plan selector ─── */}
            <div className="bg-surface-card rounded-card p-5 mb-4">
                <div className="flex items-center gap-2 mb-3">
                    <Crown size={18} className="text-daisies" />
                    <h3 className="font-semibold text-text-primary">Elige tu membresía</h3>
                </div>
                <div className={`grid gap-2 ${plans.length >= 3 ? 'grid-cols-3' : plans.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {plans.map((plan) => {
                        const isActive = selectedPlanId === plan.id;
                        return (
                            <button
                                key={plan.id}
                                onClick={() => {
                                    setSelectedPlanId(plan.id);
                                    setAppliedCoupon(null); // reset cupón al cambiar plan
                                }}
                                className={`press-scale-card relative rounded-input px-3 py-3 text-center transition-all ${
                                    isActive
                                        ? 'bg-daisies text-white ring-2 ring-brand'
                                        : 'bg-warm-canvas text-text-primary'
                                }`}
                            >
                                {plan.isRecommended && (
                                    <span className={`absolute -top-2 left-1/2 -translate-x-1/2 ds-caption font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                                        isActive ? 'bg-white text-daisies' : 'bg-daisies text-white'
                                    }`}>
                                        Recomendado
                                    </span>
                                )}
                                <div className={`text-[11px] font-medium uppercase tracking-wider ${isActive ? 'text-white/90' : 'text-text-secondary'}`}>
                                    {plan.name}
                                </div>
                                <div className={`mt-1 font-bold ${isActive ? 'text-white' : 'text-text-primary'}`}>
                                    ${Number(plan.price).toLocaleString('es-MX')}
                                </div>
                                <div className={`ds-caption mt-0.5 ${isActive ? 'text-white/80' : 'text-text-secondary'}`}>
                                    {periodLabel(plan.months)}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Resumen del plan activo */}
                <div className="mt-4 pt-3 border-t border-brand-100 flex items-center gap-3">
                    <div className="w-10 h-10 bg-daisies rounded-input flex items-center justify-center flex-shrink-0">
                        <Crown size={18} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-text-primary truncate text-sm">{currentPlan?.name}</p>
                        <p className="text-text-secondary text-xs">70 min de consultas · Todas las especialidades · Cancela cuando quieras</p>
                    </div>
                </div>
            </div>

            {/* ─── Price breakdown ─── */}
            <div className="bg-white rounded-card p-5 mb-4">
                <div className="flex justify-between items-center text-sm mb-2">
                    <span className="text-text-secondary">{currentPlan?.name}</span>
                    <span className="font-medium text-text-primary">${originalAmount.toFixed(2)}</span>
                </div>

                {appliedCoupon && (
                    <div className="flex justify-between items-center text-sm mb-2">
                        <span className="text-mint-deep flex items-center gap-1">
                            <Tag size={12} />
                            {appliedCoupon.code}
                        </span>
                        <span className="font-medium text-mint-deep">
                            -{appliedCoupon.discountType === 'amount'
                                ? `$${appliedCoupon.discountAmount.toFixed(2)}`
                                : `${appliedCoupon.discountPercentage}%`}
                        </span>
                    </div>
                )}

                <div className="flex justify-between items-center pt-3 border-t border-brand-100">
                    <span className="font-semibold text-text-primary">Total</span>
                    <span className="text-xl font-bold text-daisies">
                        ${totalAmount.toFixed(2)} MXN
                        <span className="text-xs font-medium text-text-secondary ml-1">
                            {periodLabel(currentPlan?.months)}
                        </span>
                    </span>
                </div>

                {/* Coupon toggle */}
                {!appliedCoupon && (
                    <button
                        onClick={() => setShowCoupon(!showCoupon)}
                        className="mt-3 text-sm text-daisies font-medium hover:text-daisies-hover transition-colors"
                    >
                        {showCoupon ? 'Ocultar cupón' : '¿Tienes un cupón?'}
                    </button>
                )}

                {/* Coupon input */}
                {showCoupon && !appliedCoupon && (
                    <div className="mt-3 pt-3 border-t border-brand-100">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={couponCode}
                                onChange={(e) => {
                                    setCouponCode(e.target.value.toUpperCase());
                                    setCouponError('');
                                }}
                                placeholder="Código de descuento"
                                className="flex-1 px-3 py-2.5 bg-warm-canvas rounded-input text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-all uppercase"
                                disabled={validatingCoupon}
                            />
                            <button
                                onClick={handleValidateCoupon}
                                disabled={validatingCoupon || !couponCode.trim()}
                                className="px-4 py-2.5 bg-daisies text-white rounded-input font-semibold text-sm hover:bg-daisies-hover transition-all disabled:opacity-40 active:scale-[0.97] flex items-center gap-1.5"
                            >
                                {validatingCoupon ? (
                                    <Loader className="animate-spin" size={14} />
                                ) : (
                                    'Aplicar'
                                )}
                            </button>
                        </div>
                        {couponError && (
                            <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                                <X size={11} />
                                {couponError}
                            </p>
                        )}
                    </div>
                )}

                {/* Applied coupon badge */}
                {appliedCoupon && (
                    <div className="mt-3 flex items-center justify-between bg-mint-pill rounded-input px-3 py-2">
                        <div className="flex items-center gap-2">
                            <CheckCircle size={14} className="text-mint-deep" />
                            <span className="text-sm font-medium text-mint-deep">{appliedCoupon.code}</span>
                        </div>
                        <button
                            onClick={handleRemoveCoupon}
                            className="text-text-mute hover:text-red-500 transition-colors"
                        >
                            <X size={14} />
                        </button>
                    </div>
                )}
            </div>

            {/* ─── Card form ─── */}
            <div className="bg-white rounded-card p-5 mb-4">
                <div className="flex items-center gap-2 mb-5">
                    <CreditCard size={18} className="text-daisies" />
                    <h3 className="font-semibold text-text-primary">Tarjeta de pago</h3>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">
                            Número de tarjeta
                        </label>
                        <input
                            type="text"
                            value={cardData.number}
                            onChange={(e) => handleCardInputChange('number', e.target.value)}
                            placeholder="0000 0000 0000 0000"
                            className="w-full px-4 py-3 bg-warm-canvas rounded-input text-base focus:outline-none focus:ring-2 focus:ring-brand transition-all"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">
                            Nombre en la tarjeta
                        </label>
                        <input
                            type="text"
                            value={cardData.name}
                            onChange={(e) => handleCardInputChange('name', e.target.value)}
                            placeholder="NOMBRE APELLIDO"
                            className="w-full px-4 py-3 bg-warm-canvas rounded-input text-base focus:outline-none focus:ring-2 focus:ring-brand transition-all"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">
                                Vencimiento
                            </label>
                            <input
                                type="text"
                                value={cardData.expiry}
                                onChange={(e) => handleCardInputChange('expiry', e.target.value)}
                                placeholder="MM/AA"
                                className="w-full px-4 py-3 bg-warm-canvas rounded-input text-base focus:outline-none focus:ring-2 focus:ring-brand transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">
                                CVV
                            </label>
                            <input
                                type="text"
                                value={cardData.cvv}
                                onChange={(e) => handleCardInputChange('cvv', e.target.value)}
                                placeholder="123"
                                className="w-full px-4 py-3 bg-warm-canvas rounded-input text-base focus:outline-none focus:ring-2 focus:ring-brand transition-all"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Pay button ─── */}
            <button
                onClick={handlePayment}
                disabled={loading}
                className="w-full py-4 bg-daisies text-white rounded-card font-bold text-[17px] hover:bg-daisies-hover active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 mb-3"
            >
                {loading ? (
                    <>
                        <Loader className="animate-spin" size={20} />
                        Procesando...
                    </>
                ) : (
                    <>
                        <Lock size={17} />
                        Suscribirme por ${totalAmount.toFixed(2)} MXN{periodLabel(currentPlan?.months)}
                    </>
                )}
            </button>

            {/* Trust footer */}
            <div className="flex items-center justify-center gap-1.5 text-text-secondary text-[11px]">
                <Shield size={11} />
                <span>
                    Pago seguro con encriptación SSL ·{' '}
                    <a href="#" className="text-daisies hover:underline">Términos</a> ·{' '}
                    <a href="#" className="text-daisies hover:underline">Privacidad</a>
                </span>
            </div>
        </div>
    );
};

export default MembershipCheckout;
